"use client";

/**
 * DeskToast — the desk's own voice (SIG-1214 redesign, operator-personal).
 *
 * Operator ruling (live-test 2026-08-18): this rounded onyx card is THE toast
 * — every lane renders it; no rectangular variant survives. Donna fronts the
 * benign/celebratory tones; risk tones (ice/rose) drop the avatar so no
 * mascot fronts a warning or a halt (SIG-1214 rule, unchanged). Mint is the
 * celebration accent, ice the attention accent. Rides sonner
 * (conventions: Sonner over shadcn Toast) — a STABLE id makes sonner replace
 * the toast in place, which is what entity-supersede needs.
 */
import type * as React from "react";
import { toast } from "sonner";
import { agentPersona, type AgentPersonaKey } from "@/lib/agent-personas";
const TONE = {
  mint: {
    accent: "hsl(160 65% 55%)",
    ring: "0 0 0 1px hsl(160 65% 55% / 0.35)"
  },
  ice: {
    accent: "#4DA6FF",
    ring: "0 0 0 1px rgba(77, 166, 255, 0.4)"
  },
  neutral: {
    accent: "rgba(255,255,255,0.55)",
    ring: "0 0 0 1px rgba(255,255,255,0.12)"
  },
  // ROSE is the one halt accent (operator round 3, 2026-08-18).
  rose: {
    accent: "hsl(350 80% 62%)",
    ring: "0 0 0 1px hsl(350 80% 62% / 0.45)"
  }
} as const;
export type DeskToastTone = keyof typeof TONE;
export interface DeskToastProps {
  eyebrow: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  tone?: DeskToastTone;
  /** Donna fronts benign tones; risk tones pass false (no mascot on halts). */
  avatar?: boolean;
  /** Speaking agent for the avatar (operator ruling 2026-08-18) — canonical
   * portraits only. Defaults to Donna, the desk's front-of-house voice. */
  persona?: AgentPersonaKey;
  /** One plain action button — never a radix ToastAction (needs no context). */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Explicit close affordance — set by showDeskToast for persistent
   * (action-bearing) cards, which sonner gives no close button. */
  onDismiss?: () => void;
}
export function DeskToastCard({
  eyebrow,
  title,
  description,
  tone = "neutral",
  avatar = true,
  persona = "donna",
  action,
  onDismiss
}: DeskToastProps) {
  const palette = TONE[tone];
  const speaker = agentPersona(persona, "donna");
  return <div data-testid="desk-toast" className="desk-pill-beam flex w-[356px] items-center gap-3 rounded-2xl bg-[#0b0f14]/95 p-3.5 backdrop-blur-md" style={{
    boxShadow: `${palette.ring}, 0 12px 40px rgba(0,0,0,0.55)`,
    ["--sv-beam-color" as string]: palette.accent
  }} data-sentry-component="DeskToastCard" data-sentry-source-file="desk-toast.tsx">
      {avatar ?
    // eslint-disable-next-line @next/next/no-img-element
    <img src={speaker.portrait} alt={speaker.name} width={40} height={40} className="size-10 shrink-0 rounded-full object-cover" style={{
      boxShadow: `0 0 0 1.5px ${palette.accent}`
    }} /> : <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{
      backgroundColor: palette.accent
    }} />}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{
        color: palette.accent
      }}>
          {eyebrow}
        </div>
        <div className="truncate text-sm font-semibold text-white/95">{title}</div>
        {description ? <div className="truncate text-xs text-white/55">{description}</div> : null}
      </div>
      {action ? <button type="button" onClick={action.onClick} className="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold text-white/85 transition-colors hover:bg-white/10" style={{
      borderColor: palette.accent
    }}>
          {action.label}
        </button> : null}
      {onDismiss ? <button type="button" aria-label="Dismiss notification" onClick={onDismiss} className="shrink-0 rounded-full px-1.5 py-1 text-[13px] leading-none text-white/40 transition-colors hover:text-white">
          ✕
        </button> : null}
    </div>;
}
export function showDeskToast(props: DeskToastProps & {
  id?: string | number;
  duration?: number;
}): string | number {
  const {
    id,
    duration,
    ...card
  } = props;
  // Gap-analysis R1 (consensus): the no-auto-dismiss rule for action toasts
  // must live HERE — the live websocket-alert lane calls showDeskToast
  // directly, so a legacy-shim-only default left the operator's actual
  // toasts auto-closing at 5s. And an unkillable toast is worse than a
  // vanishing one: the action click dismisses the card, and every
  // action-bearing pill carries an explicit ✕ (sonner renders no close
  // button for custom cards).
  const toastId = id ?? `desk-toast-${Math.random().toString(36).slice(2, 10)}`;
  const action = card.action ? {
    ...card.action,
    onClick: () => {
      card.action?.onClick();
      toast.dismiss(toastId);
    }
  } : undefined;
  return toast.custom(() => <DeskToastCard {...card} action={action} onDismiss={card.action ? () => toast.dismiss(toastId) : undefined} />, {
    id: toastId,
    duration: duration ?? (card.action ? Infinity : 5000),
    // Pill-only (operator screenshot round 2026-08-18): `unstyled` makes the
    // sonner wrapper contribute ZERO box — no width/padding/background from
    // sonner's own stylesheet, so nothing rectangular exists behind the pill
    // and the border beam rides the pill's own radius. The className keeps
    // the belt-and-suspenders CSS override for anything the flag misses.
    unstyled: true,
    className: "sigma-toast--pill"
  });
}